<!--
 * @Description: 面包屑 -- 目前只做最多三级，首页 > 上一级页面 > 当前页面
 * @Author: xiuwen
 * @Date: 2025-01-04 15:58:50
 * @LastEditors: zishu
 * @LastEditTime: 2026-07-21 18:08:00
-->
<template>
	<div class="pc-breadcrumb-bar">
		<div class="pc-breadcrumb-bar__nav">
			<n-breadcrumb separator=">">
				<n-breadcrumb-item @click="toHome" class="breadcrumb-custom">
					{{ homeLabel }}
				</n-breadcrumb-item>
				<n-breadcrumb-item v-if="shouldShowBackName" @click="toBack" class="breadcrumb-custom">
					{{ displayBackNameTranslated }}
				</n-breadcrumb-item>
				<n-breadcrumb-item class="breadcrumb-current">
					<span class="breadcrumb-current__text" :title="displayCurrentName">{{
						displayCurrentNameShort
					}}</span>
				</n-breadcrumb-item>
			</n-breadcrumb>
		</div>

		<SquareSearchBar v-if="showSearch" trigger-only @open="openSearchModal" />
	</div>

	<PcSearchModal
		v-model:show="showSearchModal"
		search-tracking-scope="media_square"
		trigger-position="breadcrumb_search"
	/>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import SquareSearchBar from "~/components/pc/square-search-bar/index.vue";
import PcSearchModal from "~/components/pc/header/pc-search-modal.vue";
import { useI18n } from "vue-i18n";

const { t, te } = useI18n();

/** 当前标题超过该长度时截断并加省略号（CSS 省略不可靠时的兜底） */
const CURRENT_NAME_MAX_LENGTH = 40;

const BREADCRUMB_LABEL_ALIASES = {
	"mHeader.tutor": "common.breadcrumb.tutor",
	導師服務: "common.breadcrumb.tutor",
	导师服务: "common.breadcrumb.tutor",
	老師服務: "common.teacherService.breadcrumb",
	老师服务: "common.teacherService.breadcrumb",
	老師詳情: "teacherDetail.pc.breadcrumb",
	老师详情: "teacherDetail.pc.breadcrumb",
	個人中心: "common.breadcrumb.userCenter",
	个人中心: "common.breadcrumb.userCenter",
	首頁: "common.breadcrumb.home",
	首页: "common.breadcrumb.home",
	AI對話: "common.breadcrumb.aiChat",
	AI对话: "common.breadcrumb.aiChat",
	聊天記錄: "common.breadcrumb.chatHistory",
	聊天记录: "common.breadcrumb.chatHistory",
	線上測試: "common.breadcrumb.onlineTest",
	线上测试: "common.breadcrumb.onlineTest",
	媒體廣場: "square.breadcrumb",
	媒体广场: "square.breadcrumb",
	播客: "square.tabs.podcast",
	圖文: "square.tabs.article",
	图文: "square.tabs.article",
	Shorts: "square.tabs.shorts",
};

function translateBreadcrumbLabel(key) {
	if (!key || typeof key !== "string") return key;
	const aliasKey = BREADCRUMB_LABEL_ALIASES[key];
	if (aliasKey && te(aliasKey)) return t(aliasKey);
	return te(key) ? t(key) : key;
}

const props = defineProps({
	name: {
		type: String,
		default: () => {
			const route = useRoute(); // Nuxt's useRoute
			return route.meta?.breadcrumbName || "";
		},
	},
	backName: {
		type: String,
		default: "",
	},
	backPath: {
		type: String,
		default: "",
	},
	showSearch: {
		type: Boolean,
		default: false,
	},
});

const showSearchModal = ref(false);

const route = useRoute(); // Nuxt's useRoute

const router = useRouter(); // Nuxt's useRouter

// 只在客户端获取路由历史信息
const backRouteName = ref("");
const displayBackName = computed(() => props.backName || backRouteName.value);
const displayBackNameTranslated = computed(() => translateBreadcrumbLabel(displayBackName.value));
const homeLabel = computed(() => t("common.breadcrumb.home"));

const shouldShowBackName = computed(() => {
	const back = displayBackName.value;
	if (!back) return false;
	if (back === "首頁" || back === "common.breadcrumb.home") return false;
	return displayBackNameTranslated.value !== homeLabel.value;
});

const displayCurrentName = computed(() => {
	const raw = props.name || route.meta?.breadcrumbName || "";
	return translateBreadcrumbLabel(raw);
});

const displayCurrentNameShort = computed(() => {
	const text = displayCurrentName.value;
	if (!text || text.length <= CURRENT_NAME_MAX_LENGTH) return text;
	return `${text.slice(0, CURRENT_NAME_MAX_LENGTH)}...`;
});

onMounted(() => {
	if (!import.meta.client || props.backName) return;
	const { routes, history } = router.options;
	backRouteName.value = findMostMatchedRoute(routes, history.state.back);
});

function findMostMatchedRoute(routes, pathToMatch) {
	if (!pathToMatch) return;
	let mostMatchedRoute = null;
	let maxMatchLength = 0;

	routes.forEach(route => {
		const match = pathToMatch.match(new RegExp(`^${route.path.replace(/:\w+/g, "([^/]+)")}`));
		if (match && match[0].length >= maxMatchLength) {
			mostMatchedRoute = route;
			maxMatchLength = match[0].length;
		}
	});

	return mostMatchedRoute?.meta?.breadcrumbName || "";
}

const toHome = async () => {
	await navigateTo("/");
};

const toBack = async () => {
	if (props.backPath) {
		await navigateTo(props.backPath);
		return;
	}
	router.go(-1);
};

const openSearchModal = () => {
	showSearchModal.value = true;
};
</script>

<style lang="scss" scoped>
.pc-breadcrumb-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;

	&__nav {
		flex: 1;
		min-width: 0;

		:deep(.n-breadcrumb) {
			display: flex;
			flex-wrap: nowrap;
			align-items: center;
			white-space: nowrap;
		}

		:deep(.n-breadcrumb-item) {
			display: inline-flex;
			flex: 0 0 auto;
			align-items: center;
		}

		:deep(.n-breadcrumb-item__separator) {
			display: inline-flex;
			align-items: center;
			margin: 0 8px;
		}
	}
}

:deep(.n-breadcrumb-item__link) {
	display: inline-block;
	vertical-align: bottom;
}

:deep(.breadcrumb-custom .n-breadcrumb-item__link) {
	color: #22262a;
	font-weight: bold;
	font-size: 20px;
}

:deep(.breadcrumb-current),
:deep(.breadcrumb-current .n-breadcrumb-item__link) {
	color: #f2af19 !important;
	font-size: 20px;
}

:deep(.breadcrumb-current__text) {
	display: inline-block;
	max-width: 30em;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: bottom;
}

:deep(.n-breadcrumb-item:last-child .n-breadcrumb-item__separator) {
	display: none !important;
}
</style>
